// src/components/StoreClosedModal/index.js
// ✅ UPDATED: Show pre-order button only if store supports it
import React from 'react';
import { X, ShoppingBag, Store, Clock, MapPin } from 'lucide-react';
import './index.css';

const StoreClosedModal = ({
  isOpen,
  onClose,
  store,
  storeStatus,
  onPreOrder,
  supportsPreOrder = false, // ✅ NEW PROP: Whether store supports pre-orders
  hasOtherStores = true // Whether "Choose Another Store" makes sense — false for a single-store restaurant
}) => {
  if (!isOpen || !store || !storeStatus) return null;

  const handlePreOrder = () => {
    console.log('🛍️ Pre-order initiated for:', store.name);
    onPreOrder();
  };

  const formatNextOpenTime = () => {
    if (!storeStatus.nextOpenTime) return 'Soon';

    if (storeStatus.nextOpenTime.includes('at')) {
      return storeStatus.nextOpenTime;
    }

    return `at ${storeStatus.nextOpenTime}`;
  };

  // formatNextOpenTime() is capitalized for use as its own sentence
  // ("Tomorrow at 7:01 AM", "Monday at 9:00 AM", "Soon") — lowercase the
  // leading word so it reads naturally mid-sentence after "It will reopen "
  // in the single-store message below (e.g. "reopen tomorrow at 7:01 AM").
  const lowercaseFirst = (str) => str.charAt(0).toLowerCase() + str.slice(1);

  return (
    <div className="store-closed-overlay" onClick={onClose}>
      <div className="store-closed-modal" onClick={(e) => e.stopPropagation()}>
        {/* Hanging Hook */}
        <div className="store-closed-hook"></div>

        {/* Hanging Banner with "Store Closed" */}
        <div className="store-closed-hanging-banner">
          <div className="store-closed-banner-text">
            <span className="store-closed-banner-main">STORE</span>
            <span className="store-closed-banner-sub">CLOSED</span>
          </div>
        </div>

        <button
          onClick={onClose}
          className="store-closed-close"
          aria-label="Close modal"
        >
          <X size={18} />
        </button>

        <div className="store-closed-content">
          {/* Opening Time - Featured at Top */}
          <div className="store-closed-opening-card">
            <Clock size={20} className="store-closed-clock-icon" />
            <p className="store-closed-opening-text">
              {hasOtherStores
                ? (storeStatus.message || `Store is currently closed. Opens ${formatNextOpenTime()}`)
                : `The ${store.name} store is currently closed. It will reopen ${lowercaseFirst(formatNextOpenTime())}.`}
            </p>
          </div>

          {/* Store Information */}
          <div className="store-closed-info-card">
            <div className="store-closed-store-name">{store.name}</div>
            <div className="store-closed-store-address">
              <MapPin size={13} />
              <span>{store.location}</span>
            </div>
          </div>

          {/* ✅ CONDITIONAL: Show pre-order availability message */}
          {supportsPreOrder ? (
            <div className="store-closed-preorder-info">
              <ShoppingBag size={15} />
              <span>Pre-orders available - Schedule for later!</span>
            </div>
          ) : (
            <div className="store-closed-no-preorder-info">
              <ShoppingBag size={15} />
              <span>Pre-orders not available at this location</span>
            </div>
          )}
        </div>

        {/* Action Buttons - Conditional Pre-Order Button. Skip the footer
            entirely (rather than render it empty) for a single store with
            no pre-order support — nothing left to put in it once "Choose
            Another Store" is hidden. */}
        {(supportsPreOrder || hasOtherStores) && (
        <div className="store-closed-footer">
          {/* ✅ CONDITIONAL: Only show Schedule Order button if supported */}
          {supportsPreOrder && (
            <button
              className="store-closed-back-btn primary"
              onClick={handlePreOrder}
            >
              <ShoppingBag size={18} />
              <span>Schedule Order</span>
            </button>
          )}

          {/* Only makes sense when there's actually another store to switch to */}
          {hasOtherStores && (
            <button
              className="store-closed-back-btn secondary"
              onClick={onClose}
            >
              <Store size={18} />
              <span>Choose Another Store</span>
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default StoreClosedModal;